import { useEffect, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import { Map, List } from 'lucide-react';
import type { QuoteSummaryDto } from '../types';
import { QuoteStatus } from '../types';
import { MAP_STYLE_URL } from '../lib/mapLibre';

function emojiPinElement(emoji: string, color: string): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    `background:${color};color:white;border-radius:50%;width:32px;height:32px;` +
    'display:flex;align-items:center;justify-content:center;font-size:15px;' +
    'border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3);cursor:pointer;';
  el.textContent = emoji;
  return el;
}

interface WorkshopQuoteMapViewProps {
  vehicleLat: number;
  vehicleLng: number;
  vehicleAddress: string;
  quotes: QuoteSummaryDto[];
  /** Called when a workshop marker is clicked — scroll the list to that quote. */
  onQuoteSelect?: (quoteId: string) => void;
}

/** Leaflet map for workshop/repair job quotes. Shows the vehicle location and, when
 *  workshop coordinates are available, a pin for each workshop bidder. */
export function WorkshopQuoteMapView({
  vehicleLat, vehicleLng, vehicleAddress, quotes, onQuoteSelect,
}: WorkshopQuoteMapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef       = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [vehicleLng, vehicleLat],
      zoom: 12,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      const bounds = new maplibregl.LngLatBounds([vehicleLng, vehicleLat], [vehicleLng, vehicleLat]);

      // Vehicle / job location pin
      const vehiclePopup = new maplibregl.Popup()
        .setHTML(`<b>Vehicle Location</b><br><span style="font-size:12px">${vehicleAddress}</span>`);
      new maplibregl.Marker({ element: emojiPinElement('🚗', '#16a34a') })
        .setLngLat([vehicleLng, vehicleLat])
        .setPopup(vehiclePopup)
        .addTo(map);
      vehiclePopup.addTo(map);

      // Workshop pins (only shown when coordinates are available)
      const quotesWithCoords = quotes.filter(
        q => q.bidderLat != null && q.bidderLng != null && q.status !== QuoteStatus.Rejected && q.status !== QuoteStatus.Withdrawn
      );

      quotesWithCoords.forEach(q => {
        const colour = q.status === QuoteStatus.Accepted ? '#16a34a' : '#1d4ed8';
        const el = emojiPinElement('🔧', colour);

        const labourLine = q.labourAmount != null
          ? `<br>Labour: <b>£${q.labourAmount.toFixed(2)}</b> · Parts: <b>£${(q.partsAmount ?? 0).toFixed(2)}</b>`
          : '';
        const stars = '★'.repeat(Math.round(q.averageCarrierRating)) + '☆'.repeat(5 - Math.round(q.averageCarrierRating));

        const popup = new maplibregl.Popup({ maxWidth: '200px' }).setHTML(
          `<b>${q.carrierName}</b>${labourLine}` +
          `<br>Total: <b>£${q.amount.toFixed(2)}</b>` +
          `<br><span style="color:#f59e0b">${stars}</span> ${q.averageCarrierRating.toFixed(1)}` +
          `<br><a href="#" style="color:#1d4ed8;font-size:11px" onclick="event.preventDefault()">View quote</a>`
        );

        new maplibregl.Marker({ element: el }).setLngLat([q.bidderLng!, q.bidderLat!]).setPopup(popup).addTo(map);

        if (onQuoteSelect) {
          el.addEventListener('click', () => onQuoteSelect(q.id));
        }

        bounds.extend([q.bidderLng!, q.bidderLat!]);
      });

      map.fitBounds(bounds, { padding: 48, maxZoom: quotesWithCoords.length === 0 ? 13 : 12 });
      setTimeout(() => map.resize(), 150);
    });

    return () => { mapRef.current?.remove(); mapRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={containerRef} className="h-72 w-full" />;
}

// ─────────────────────────────────────────────────────────────────────────────
//  WRAPPER: List / Map toggle for workshop job quotes
// ─────────────────────────────────────────────────────────────────────────────
interface WorkshopQuotesPanelHeaderProps {
  quoteCount: number;
  view: 'list' | 'map';
  onToggle: (v: 'list' | 'map') => void;
}

export function WorkshopQuotesPanelHeader({ quoteCount, view, onToggle }: WorkshopQuotesPanelHeaderProps) {
  return (
    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
      <h2 className="font-semibold text-gray-900">
        Quotes Received ({quoteCount})
      </h2>
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
        <button
          onClick={() => onToggle('list')}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors font-medium ${
            view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <List className="w-3.5 h-3.5" /> List
        </button>
        <button
          onClick={() => onToggle('map')}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md transition-colors font-medium ${
            view === 'map' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Map className="w-3.5 h-3.5" /> Map
        </button>
      </div>
    </div>
  );
}
